import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    IconButton,
    Tooltip,
    TablePagination,
    TextField,
    FormControlLabel,
    Switch,
    Typography,
    CircularProgress,
    Stack,
    Toolbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Divider,
} from "@mui/material";

import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import { visuallyHidden } from '@mui/utils';
import { useState, useEffect, useMemo } from "react";
import CircleIcon from '@mui/icons-material/Circle';
import { useNavigate } from 'react-router-dom';
import { useToken } from '../items/UseToken';
import { WorkersReadyState } from '../ReadyStates';

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = (array || []).map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: 'name', numeric: false, disablePadding: false, label: 'Name' },
    { id: 'guid', numeric: false, disablePadding: false, label: 'ID' },
    { id: 'url', numeric: false, disablePadding: false, label: 'Url' },
    { id: 'create_date', numeric: false, disablePadding: false, label: 'Create Date' },
    { id: 'is_online', numeric: false, disablePadding: false, label: 'Status' },
];

function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableRow>
            <TableCell padding="checkbox" sx={{ bgcolor: "background.paper" }}>
                <Checkbox
                    color="primary"
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    onChange={onSelectAllClick}
                    inputProps={{ 'aria-label': 'select all machines' }}
                />
            </TableCell>

            {headCells.map((headCell) => (
                <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    padding={headCell.disablePadding ? 'none' : 'normal'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                        bgcolor: "background.paper",
                        fontWeight: 900,
                        letterSpacing: 0.3,
                        whiteSpace: "nowrap",
                    }}
                >
                    <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={createSortHandler(headCell.id)}
                    >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                        ) : null}
                    </TableSortLabel>
                </TableCell>
            ))}
        </TableRow>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function MachinesTable({ emptyState }) {
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('start_date');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [dense, setDense] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [rows, setRows] = useState([]);
    const [showFilter, setShowFilter] = useState(false);

    const [nameFilter, setNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [idFilter, setIdFilter] = useState('');
    const [startDateFilter, setStarDateFilter] = useState('');

    // ✅ Confirm delete dialog state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    const navigate = useNavigate();
    const { token } = useToken();

    const handleNameFilterChange = (event) => setNameFilter(event.target.value);
    const handleStatusFilterChange = (event) => setStatusFilter(event.target.value);
    const handleIdFilterChange = (event) => setIdFilter(event.target.value);
    const handleStarDateFilterChange = (event) => setStarDateFilter(event.target.value);

    const handleShowFilterChange = () => setShowFilter((prev) => !prev);

    const clearFilters = () => {
        setNameFilter("");
        setStatusFilter("");
        setIdFilter("");
        setStarDateFilter("");
        setPage(0);
    };

    const hasAnyFilter = useMemo(() => {
        return (
            (nameFilter ?? "").length > 0 ||
            (statusFilter ?? "").length > 0 ||
            (idFilter ?? "").length > 0 ||
            (startDateFilter ?? "").length > 0
        );
    }, [nameFilter, statusFilter, idFilter, startDateFilter]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const isSelected = (guid) => selected.indexOf(guid) !== -1;

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = visibleRows.map((n) => n.guid).filter(Boolean);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, guid) => {
        const selectedIndex = selected.indexOf(guid);
        let newSelected = [];

        if (selectedIndex === -1) newSelected = newSelected.concat(selected, guid);
        else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
        else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
        else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeDense = (event) => setDense(event.target.checked);

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const fetchMachines = () => {
        navigate('/resources/workers');

        const params = [
            ['name', nameFilter],
            ['is_online', statusFilter],
            ['guid', idFilter],
            ['create_date', startDateFilter],
        ];

        let queryParams = "";
        params.forEach((param) => {
            if ((param[1] || "").length > 1) {
                queryParams = queryParams + param[0] + "=" + encodeURIComponent(param[1]) + "&";
            }
        });

        queryParams = queryParams.endsWith("&") ? queryParams.slice(0, -1) : queryParams;
        const query = queryParams ? `api/getmachines?${queryParams}` : "api/getmachines";

        setIsLoaded(false);
        fetch(query, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token,
            }
        })
            .then(res => res.json())
            .then(res => { setRows(res.results || []); setIsLoaded(true); })
            .catch(err => { console.log(err); setIsLoaded(true); });
    };

    useEffect(() => {
        fetchMachines();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameFilter, statusFilter, idFilter, startDateFilter]);

    // ✅ open confirm dialog
    const openDeleteConfirm = () => {
        setDeleteError("");
        setConfirmOpen(true);
    };

    // ✅ close confirm dialog
    const closeDeleteConfirm = () => {
        if (deleting) return;
        setConfirmOpen(false);
    };

    // ✅ actual delete + refresh
    const confirmDeleteMachines = async () => {
        if (selected.length === 0) {
            setConfirmOpen(false);
            return;
        }

        setDeleting(true);
        setDeleteError("");

        try {
            await Promise.all(
                selected.map((guid) =>
                    fetch('api/deletemachine', {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + token,
                        },
                        body: JSON.stringify({ guid }),
                    }).then(async (r) => {
                        if (!r.ok) {
                            const txt = await r.text().catch(() => "");
                            throw new Error(txt || `Delete failed for ${guid}`);
                        }
                    })
                )
            );

            setSelected([]);
            setConfirmOpen(false);
            fetchMachines();
        } catch (e) {
            setDeleteError(e?.message || "Failed to delete one or more machines.");
        } finally {
            setDeleting(false);
        }
    };

    // 🔎 lokalne filtrowanie (żeby działało też select-all po filtrach)
    const visibleRows = useMemo(() => {
        return rows.filter((r) => {
            const nameOk = !nameFilter || (r.name || "").toLowerCase().includes(nameFilter.toLowerCase());
            const idOk = !idFilter || (r.guid || "").toLowerCase().includes(idFilter.toLowerCase());
            const statusOk = !statusFilter || String(r.is_online ?? "").toLowerCase().includes(statusFilter.toLowerCase());
            const dateOk = !startDateFilter || String(r.create_date ?? "").toLowerCase().includes(startDateFilter.toLowerCase());
            return nameOk && idOk && statusOk && dateOk;
        });
    }, [rows, nameFilter, idFilter, statusFilter, startDateFilter]);

    // ✅ po zmianie filtrów usuń zaznaczenie elementów, których nie widać
    useEffect(() => {
        const visibleIds = new Set(visibleRows.map((r) => r.guid));
        setSelected((prev) => prev.filter((id) => visibleIds.has(id)));
    }, [visibleRows]);

    const showEmpty = rows.length === 0;

    const EnhancedTableToolbar = (props) => {
        const { numSelected } = props;

        return (
            <Toolbar
                sx={{
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                    ...(numSelected > 0 && {
                        bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                    }),
                }}
            >
                {numSelected > 0 ? (
                    <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
                        {numSelected} selected
                    </Typography>
                ) : (
                    <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div" />
                )}

                {numSelected > 0 && (
                    <Tooltip title="Delete">
                        <IconButton onClick={openDeleteConfirm}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Toolbar>
        );
    };

    EnhancedTableToolbar.propTypes = {
        numSelected: PropTypes.number.isRequired,
    };

    return (
        <>
            {!isLoaded ? (
                <Box sx={{ p: 10, alignItems: "center" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={30} thickness={5} />
                        <Typography variant="header" color="text.secondary">
                            Loading Workers data
                        </Typography>
                    </Stack>
                </Box>
            ) : showEmpty ? (
                <Box sx={{ p: 4 }}>
                    {emptyState}
                </Box>
            ) : (
                <>
                    <WorkersReadyState />

                    <Box sx={{ width: '100%' }}>
                        <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{
                                width: "100%",
                                mb: 2,
                                borderRadius: 3,
                                overflow: "hidden",
                            }}
                        >
                            {/* Top bar (ten sam vibe co w ProcessTable / GenericDataTable) */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }} variant="subtitle1">
                                    Workers
                                </Typography>

                                <Box sx={{ flex: 1 }} />

                                {selected.length > 0 && (
                                    <Chip size="small" label={`${selected.length} selected`} sx={{ mr: 0.5 }} />
                                )}

                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchMachines}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                        size="small"
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={showFilter ? "Hide filters" : "Show filters"}>
                                    <IconButton
                                        onClick={handleShowFilterChange}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: showFilter ? "primary.main" : "divider",
                                            borderRadius: 2,
                                        }}
                                        size="small"
                                    >
                                        <FilterListIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {selected.length > 0 && (
                                    <Tooltip title={`Delete (${selected.length})`}>
                                        <IconButton
                                            onClick={openDeleteConfirm}
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "error.main",
                                                borderRadius: 2,
                                                color: "error.main",
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            <Divider />

                            {/* Filters */}
                            {showFilter && (
                                <Box sx={{ p: 2, bgcolor: "background.paper" }}>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                            Filters
                                        </Typography>

                                        <Box sx={{ flex: 1, width: "100%" }}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ py: 1 }}>
                                                                <TextField
                                                                    value={nameFilter}
                                                                    onChange={handleNameFilterChange}
                                                                    id="nameFilter"
                                                                    label="Name"
                                                                    variant="standard"
                                                                />
                                                            </TableCell>

                                                            <TableCell sx={{ py: 1 }}>
                                                                <TextField
                                                                    value={idFilter}
                                                                    onChange={handleIdFilterChange}
                                                                    id="idFilter"
                                                                    label="ID"
                                                                    variant="standard"
                                                                />
                                                            </TableCell>

                                                            <TableCell sx={{ py: 1 }}>
                                                                {/* masz tylko statusFilter string (Online/Offline), zostawiam tekstowo */}
                                                                <TextField
                                                                    value={statusFilter}
                                                                    onChange={handleStatusFilterChange}
                                                                    id="statusFilter"
                                                                    label="Status"
                                                                    variant="standard"
                                                                />
                                                            </TableCell>

                                                            <TableCell sx={{ py: 1 }}>
                                                                <TextField
                                                                    value={startDateFilter}
                                                                    onChange={handleStarDateFilterChange}
                                                                    id="createDateFilter"
                                                                    label="Create Date"
                                                                    variant="standard"
                                                                />
                                                            </TableCell>

                                                            <TableCell align="right" sx={{ whiteSpace: "nowrap", py: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    onClick={clearFilters}
                                                                    disabled={!hasAnyFilter}
                                                                    sx={{ textTransform: "none", fontWeight: 700 }}
                                                                >
                                                                    Clear
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {/* Table */}
                            <TableContainer sx={{ maxHeight: dense ? 520 : 640 }}>
                                <Table
                                    stickyHeader
                                    sx={{ minWidth: 750 }}
                                    aria-labelledby="tableTitle"
                                    size={dense ? 'small' : 'medium'}
                                >
                                    <TableHead>
                                        <EnhancedTableHead
                                            numSelected={selected.length}
                                            order={order}
                                            orderBy={orderBy}
                                            onSelectAllClick={handleSelectAllClick}
                                            onRequestSort={handleRequestSort}
                                            rowCount={visibleRows.length}
                                        />
                                    </TableHead>

                                    <TableBody>
                                        {stableSort(visibleRows, getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row, index) => {
                                                const isItemSelected = isSelected(row.guid);
                                                const labelId = `enhanced-table-checkbox-${index}`;

                                                return (
                                                    <TableRow
                                                        hover
                                                        role="checkbox"
                                                        aria-checked={isItemSelected}
                                                        tabIndex={-1}
                                                        key={row.guid}
                                                        selected={isItemSelected}
                                                        sx={{
                                                            "&:hover td": { bgcolor: "action.hover" },
                                                        }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                onClick={(event) => handleClick(event, row.guid)}
                                                                color="primary"
                                                                checked={isItemSelected}
                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                            />
                                                        </TableCell>

                                                        <TableCell component="th" id={labelId} scope="row" padding="none">
                                                            {row.name}
                                                        </TableCell>

                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                            {row.guid}
                                                        </TableCell>

                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            <a href={"https://" + row.url} target="_blank" rel="noopener noreferrer">
                                                                {row.url}
                                                            </a>
                                                        </TableCell>

                                                        <TableCell>{row.create_date}</TableCell>

                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            {row.is_online === true && (
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <CircleIcon color="success" sx={{ fontSize: 12 }} />
                                                                    <Typography variant="body2">Online</Typography>
                                                                </Stack>
                                                            )}
                                                            {row.is_online === false && (
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <CircleIcon color="error" sx={{ fontSize: 12 }} />
                                                                    <Typography variant="body2">Offline</Typography>
                                                                </Stack>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}

                                        {emptyRows > 0 && (
                                            <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                                <TableCell colSpan={6} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={visibleRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>

                        <FormControlLabel
                            control={<Switch checked={dense} onChange={handleChangeDense} />}
                            label="Dense padding"
                        />
                    </Box>

                    {/* ✅ Confirm Delete Dialog */}
                    <Dialog open={confirmOpen} onClose={closeDeleteConfirm} maxWidth="sm" fullWidth>
                        <DialogTitle sx={{ fontWeight: 900 }}>
                            Delete {selected.length} worker{selected.length === 1 ? "" : "s"}?
                        </DialogTitle>

                        <DialogContent dividers>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                This will remove the selected worker{selected.length === 1 ? "" : "s"} from Sophona. This action cannot be undone.
                            </Typography>

                            <Box
                                sx={{
                                    mt: 1,
                                    maxHeight: 180,
                                    overflow: "auto",
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    bgcolor: "action.hover",
                                    borderRadius: 2,
                                    p: 1.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                {selected.map((id) => (
                                    <Box key={id} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {id}
                                    </Box>
                                ))}
                            </Box>

                            {deleteError && (
                                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                    {deleteError}
                                </Typography>
                            )}
                        </DialogContent>

                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={closeDeleteConfirm} disabled={deleting} sx={{ textTransform: "none" }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={confirmDeleteMachines}
                                disabled={deleting || selected.length === 0}
                                sx={{ textTransform: "none", fontWeight: 900 }}
                            >
                                {deleting ? "Deleting..." : "Delete"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </>
    );
}

MachinesTable.propTypes = {
    emptyState: PropTypes.node,
};
